import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

const mask = {
  hidden: { y: "112%" },
  show: (i) => ({ y: "0%", transition: { duration: 1.05, ease: [0.22, 1, 0.36, 1], delay: 0.2 + i * 0.14 } }),
};

const AURA = "bg-[radial-gradient(ellipse_at_72%_40%,rgba(181,149,96,0.14),transparent_55%)]";
const AURA_TAIL = "bg-[radial-gradient(ellipse_at_100%_100%,rgba(181,149,96,0.10),transparent_55%)]";

/**
 * Shared hero for the three group gateways (Eagle Inc / Eagle Eyes Group / Eagle Commerce Group).
 * Desktop (lg+) and mobile (<lg) are TWO FULLY SEPARATE TREES so a change on one can never
 * leak into the other. Geometry constants are identical on every gateway page:
 *   desktop — word block left-16 top-[15%], 11.5vw lines, medallion right-[6vw] centred at 42%
 *   mobile  — word block left-6 right-6 top-[13vh], 13vw lines, medallion right-3 below the block
 * The medallion aura renders at PAGE level (absolute inset-0 in <main>) so it flows seamlessly
 * across the hero/cards boundary — no colour seam. The desktop hero is overflow-visible so the
 * medallion's drop-shadow glow is never clipped at the hero/cards edge (no horizontal cut-off).
 * introUnderBlock: place the intro right below the word block instead of near the bottom.
 */
export default function GroupHero({
  l1,
  l2,
  l3,
  intro,
  introBottomDesktop = "11%",
  introBottomMobile = "10%",
  introUnderBlock = false,
  introBelowMedallion = false,
  compactMobile = false,
  tilt = false,
  testid,
}) {
  const heroRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 60, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 60, damping: 18 });

  const onMouseMove = (e) => {
    if (!tilt) return;
    const r = heroRef.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const lines = (smallCls) => (
    <>
      <span className="mask-line"><motion.span variants={mask} initial="hidden" animate="show" custom={0} className="whitespace-nowrap">{l1}</motion.span></span>
      {l2 ? (
        <span className="mask-line"><motion.span variants={mask} initial="hidden" animate="show" custom={1} className="text-transparent whitespace-nowrap" style={{ WebkitTextStroke: "2px rgba(244,241,235,0.9)" }}>{l2}</motion.span></span>
      ) : null}
      <span className="mask-line"><motion.span variants={mask} initial="hidden" animate="show" custom={l2 ? 2 : 1} className={`inline-block text-transparent whitespace-nowrap leading-[0.8] stroke-thin-mobile ${smallCls}`} style={{ WebkitTextStroke: "2px #B59560" }}>{l3}</motion.span></span>
    </>
  );

  const introCls = "absolute font-mono-data text-[0.62rem] tracking-[0.26em] leading-[2] text-white/45 uppercase";
  // Intro directly under the word block: offset scales with the block height (2 or 3 lines)
  const introTopDesktop = l2
    ? "top-[calc(15%+25.5vw)] [@media(max-height:920px)]:top-[calc(15%+19.2vw)]"
    : "top-[calc(15%+17vw)] [@media(max-height:920px)]:top-[calc(15%+13.5vw)]";

  return (
    <>
      {/* Page-level aura — flows from the medallion across hero AND cards, no seam */}
      <div aria-hidden="true" className={`pointer-events-none absolute inset-0 ${AURA}`} />
      <div aria-hidden="true" className={`pointer-events-none absolute inset-0 ${AURA_TAIL}`} />

      {/* ================= DESKTOP (lg and up) ================= */}
      <section ref={heroRef} onMouseMove={onMouseMove} className="hidden lg:block relative flex-1 overflow-visible" data-testid={`${testid}-hero`}>
        <h1 className="absolute left-16 top-[15%] z-10 font-gateway font-black leading-[0.8] tracking-[0.012em] text-[11.5vw] [@media(max-height:920px)]:text-[8.6vw] select-none" data-testid={`${testid}-title`}>
          {lines("text-[5.9vw] [@media(max-height:920px)]:text-[4.5vw]")}
        </h1>
        {intro && (
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className={
              introUnderBlock
                ? `${introCls} left-16 max-w-[58ch] ${introTopDesktop}`
                : `${introCls} left-16 max-w-[58ch]`
            }
            style={introUnderBlock ? undefined : { bottom: introBottomDesktop }}
            data-testid={`${testid}-intro`}
          >
            {intro}
          </motion.p>
        )}
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          style={tilt ? { rotateX: rx, rotateY: ry, transformPerspective: 900 } : undefined}
          className="pointer-events-none absolute right-[6vw] top-[42%] w-[19vw] max-w-[300px] [@media(max-height:920px)]:w-[14vw] [@media(max-height:920px)]:max-w-[220px] -translate-y-1/2"
          data-testid={`${testid}-medallion`}
        >
          <img src="/assets/medallion.png" alt="" className="w-full h-auto animate-float-slow drop-shadow-[0_40px_70px_rgba(181,149,96,0.28)]" />
        </motion.div>
      </section>

      {/* ================= MOBILE (below lg) ================= */}
      <section className={`lg:hidden relative overflow-hidden ${compactMobile ? "min-h-[max(49vh,calc(19vh+54vw))]" : "min-h-[max(54vh,calc(20vh+56vw))]"}`} data-testid={`${testid}-hero-mobile`}>
        <h1 className="absolute left-6 right-6 top-[13vh] z-10 font-gateway font-black leading-[0.8] tracking-[0.012em] text-[13vw] select-none" data-testid={`${testid}-title-mobile`}>
          {lines("text-[6.6vw]")}
        </h1>
        <img
          src="/assets/medallion.png"
          alt=""
          data-testid={`${testid}-medallion-mobile`}
          className="pointer-events-none absolute right-3 top-[calc(13vh+24vw)] w-14 h-14 animate-float-slow"
        />
        {intro && (
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className={`${introCls} left-6 ${introBelowMedallion ? "right-24" : "right-6"}`}
            style={introBelowMedallion ? { top: "calc(13vh + 24vw + 5rem)" } : { bottom: introBottomMobile }}
            data-testid={`${testid}-intro-mobile`}
          >
            {intro}
          </motion.p>
        )}
      </section>
    </>
  );
}
